import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity, Clock, Lock, RefreshCw, Send, Unlock, Wand2, X } from 'lucide-react'
import { jimiService } from '../services/jimiService'
import {
  assetPeripheralService,
  type AssetPeripheralEventRow,
} from '../services/assetPeripheralService'
import { useAuth } from '../contexts/AuthContext'

// =============================================================================
// AssetSmartLockWindow — asset-scoped smart-lock control + history
// =============================================================================
// Opened from AssetPeripheralPicker (or directly when the asset has
// exactly one SMART_LOCK peripheral). Pre-scopes everything to a single
// (assetId, imei) pair and bridges in the asset's lifetime lock-event
// history via /api/assets/:id/peripheral-events?peripheralType=SMART_LOCK.
// =============================================================================

export interface AssetSmartLockWindowProps {
  assetId: string
  imei: string
  deviceName?: string | null
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

type Tab = 'events' | 'commands' | 'offline'

const WRITE_ROLES = ['OPERATOR', 'ADMIN']

const AssetSmartLockWindow: React.FC<AssetSmartLockWindowProps> = ({
  assetId, imei, deviceName, showMessage,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const canWrite = useMemo(() => WRITE_ROLES.includes(user?.role || ''), [user])

  const [tab, setTab] = useState<Tab>('events')
  const [events, setEvents] = useState<AssetPeripheralEventRow[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [history, setHistory] = useState<any[]>([])
  const [offline, setOffline] = useState<any[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [offlineLoading, setOfflineLoading] = useState(false)
  const [busyAction, setBusyAction] = useState<string | null>(null)

  const notify = useCallback((msg: string, type: 'success' | 'error' | 'warning' | 'info') => {
    if (showMessage) showMessage(msg, type)
    else if (type === 'error') console.warn(msg)
  }, [showMessage])

  const reloadEvents = useCallback(async () => {
    setEventsLoading(true)
    try {
      const r = await assetPeripheralService.getAssetPeripheralEvents(assetId, {
        peripheralType: 'SMART_LOCK',
        limit: 200,
      })
      setEvents(r.events || [])
    } catch (err: any) {
      notify(t('asset_lock_events_load_error', 'Failed to load lock events: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally {
      setEventsLoading(false)
    }
  }, [assetId, notify, t])

  const reloadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const r = await jimiService.commandsHistory(imei, 1, 50)
      const list = r?.data?.records || r?.records || (Array.isArray(r) ? r : [])
      setHistory(list)
    } catch (err: any) {
      notify(t('asset_lock_history_load_error', 'Failed to load command history: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally {
      setHistoryLoading(false)
    }
  }, [imei, notify, t])

  const reloadOffline = useCallback(async () => {
    setOfflineLoading(true)
    try {
      const r = await jimiService.commandsOffline(imei)
      const list = r?.data?.records || r?.records || (Array.isArray(r) ? r : [])
      setOffline(list)
    } catch (err: any) {
      notify(t('asset_lock_offline_load_error', 'Failed to load offline queue: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally {
      setOfflineLoading(false)
    }
  }, [imei, notify, t])

  useEffect(() => { reloadEvents() }, [reloadEvents])
  useEffect(() => { if (tab === 'commands') reloadHistory() }, [tab, reloadHistory])
  useEffect(() => { if (tab === 'offline')  reloadOffline()  }, [tab, reloadOffline])

  const sendCommand = useCallback(async (action: 'seal' | 'unseal' | 'wakeup') => {
    if (!canWrite) return
    setBusyAction(action)
    try {
      const fn = action === 'seal' ? jimiService.commandSeal
                : action === 'unseal' ? jimiService.commandUnseal
                : jimiService.commandWakeup
      const r = await fn(imei)
      const ok = (r as any)?.code === 1000 || (r as any)?.code === 0 || (r as any)?.success === true
      notify(
        ok
          ? t('asset_lock_command_ok', '{{action}} command sent', { action })
          : t('asset_lock_command_warn', '{{action}}: {{msg}}', { action, msg: (r as any)?.message || 'queued' }),
        ok ? 'success' : 'warning')
      reloadHistory()
    } catch (err: any) {
      notify(t('asset_lock_command_error', '{{action}} failed: {{msg}}', { action, msg: err?.message || 'unknown' }), 'error')
    } finally {
      setBusyAction(null)
    }
  }, [imei, canWrite, notify, reloadHistory, t])

  const cancelOffline = useCallback(async (cmdId: string | number) => {
    if (!canWrite) return
    try {
      await jimiService.commandsOfflineCancel(imei, cmdId)
      notify(t('asset_lock_offline_cancel_ok', 'Offline command cancelled'), 'success')
      reloadOffline()
    } catch (err: any) {
      notify(t('asset_lock_offline_cancel_error', 'Cancel failed: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    }
  }, [imei, canWrite, notify, reloadOffline, t])

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-sm">{deviceName || t('asset_lock_default_name', 'Smart Lock')}</div>
            <div className="text-xs text-gray-500 font-mono">IMEI {imei} · ASSET {assetId}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canWrite || busyAction === 'seal'}
            onClick={() => sendCommand('seal')}
            title={canWrite ? '' : t('asset_lock_no_write', 'Operator role required') as string}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            <Lock className="w-4 h-4" /> {t('asset_lock_btn_seal', 'Seal')}
          </button>
          <button
            type="button"
            disabled={!canWrite || busyAction === 'unseal'}
            onClick={() => sendCommand('unseal')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            <Unlock className="w-4 h-4" /> {t('asset_lock_btn_unseal', 'Unseal')}
          </button>
          <button
            type="button"
            disabled={!canWrite || busyAction === 'wakeup'}
            onClick={() => sendCommand('wakeup')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
          >
            <Wand2 className="w-4 h-4" /> {t('asset_lock_btn_wakeup', 'Wakeup')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-1">
        {([
          { id: 'events',   label: t('asset_lock_tab_events', 'Lock events'),     icon: <Activity className="w-4 h-4" /> },
          { id: 'commands', label: t('asset_lock_tab_history', 'Command history'), icon: <Send className="w-4 h-4" /> },
          { id: 'offline',  label: t('asset_lock_tab_offline', 'Offline queue'),  icon: <Clock className="w-4 h-4" /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setTab(it.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${
              tab === it.id
                ? 'border-blue-600 text-blue-700 dark:text-blue-300'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {it.icon}{it.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={tab === 'events' ? reloadEvents : tab === 'commands' ? reloadHistory : reloadOffline}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5"
            title={t('asset_lock_refresh', 'Refresh') as string}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'events' && (
          <div>
            {eventsLoading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
            {!eventsLoading && events.length === 0 && (
              <div className="text-sm text-gray-500 italic">{t('asset_lock_events_empty', 'No lock events for this asset yet.')}</div>
            )}
            {!eventsLoading && events.length > 0 && (
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left py-2">{t('asset_lock_col_time', 'Time')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_event', 'Event')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_severity', 'Severity')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_lock', 'Lock')}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, idx) => (
                    <tr key={ev.id ?? idx} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 whitespace-nowrap text-xs text-gray-500">{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '—'}</td>
                      <td className="py-1.5">{ev.event_type || '—'}</td>
                      <td className="py-1.5">{ev.severity || '—'}</td>
                      <td className="py-1.5 font-mono text-xs">{ev.lock_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'commands' && (
          <div>
            {historyLoading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
            {!historyLoading && history.length === 0 && (
              <div className="text-sm text-gray-500 italic">{t('asset_lock_history_empty', 'No commands sent yet.')}</div>
            )}
            {!historyLoading && history.length > 0 && (
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left py-2">{t('asset_lock_col_time', 'Time')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_cmdNo', 'cmdNo')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_status', 'Status')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_ack', 'Ack')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h: any, idx: number) => (
                    <tr key={h.cmdId ?? idx} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 text-xs text-gray-500">{h.sentAt ? new Date(h.sentAt).toLocaleString() : '—'}</td>
                      <td className="py-1.5 font-mono text-xs">{h.cmdNo ?? '—'}</td>
                      <td className="py-1.5">{h.status || '—'}</td>
                      <td className="py-1.5 text-xs text-gray-500">{h.ackAt ? new Date(h.ackAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'offline' && (
          <div>
            {offlineLoading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
            {!offlineLoading && offline.length === 0 && (
              <div className="text-sm text-gray-500 italic">{t('asset_lock_offline_empty', 'No queued offline commands.')}</div>
            )}
            {!offlineLoading && offline.length > 0 && (
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left py-2">{t('asset_lock_col_time', 'Queued at')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_cmdNo', 'cmdNo')}</th>
                    <th className="text-left py-2">{t('asset_lock_col_status', 'Status')}</th>
                    <th className="text-right py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {offline.map((o: any, idx: number) => (
                    <tr key={o.cmdId ?? idx} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1.5 text-xs text-gray-500">{o.queuedAt ? new Date(o.queuedAt).toLocaleString() : '—'}</td>
                      <td className="py-1.5 font-mono text-xs">{o.cmdNo ?? '—'}</td>
                      <td className="py-1.5">{o.status || '—'}</td>
                      <td className="py-1.5 text-right">
                        <button
                          type="button"
                          disabled={!canWrite}
                          onClick={() => cancelOffline(o.cmdId)}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="w-3.5 h-3.5" /> {t('asset_lock_cancel', 'Cancel')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AssetSmartLockWindow
